import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import _ from 'lodash';

import { getPaginationTotal } from '~/modules/services/api-service';
import DownloadsPageService from '~/modules/services/downloads-page-service';
import ToastService from '~/modules/services/toast-service';
import TransferManager from '~/modules/services/transfer-manager';

const PAGE_SIZE = 100;

export default function useDownloads(userId) {

	const [downloads, setDownloads] = useState([]);
	const transfers = TransferManager.transfers;

	const [active, setActive] = useState(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [searchText, setSearchText] = useState('');
	const [total, setTotal] = useState(0);
	const [, setMostRecentLoadAt] = useState(null);

	const downloadsQuery = useQuery({
		queryKey: ['downloads', userId, searchText.trim(), currentPage],
		queryFn: () => DownloadsPageService.getAllDownloads({
			userId,
			start: currentPage * PAGE_SIZE,
			end: (currentPage * PAGE_SIZE) + ( PAGE_SIZE - 1 ),
			query: searchText.trim()
		}),
		refetchOnWindowFocus: false,
		retry: false,
		notifyOnChangeProps: ['data', 'error']
	});

	const isLoading = downloadsQuery?.isLoading || downloadsQuery?.isFetching;
	const isError = downloadsQuery?.isError;
	const refetch = downloadsQuery?.refetch;
	const fetchNextPage = () => {
		setCurrentPage(currentPage + 1);
	};
	const search = (text) => {
		setDownloads([]);
		setCurrentPage(0);
		setSearchText(text);
	};
	const nbResults = total;
	const canShowMore = downloads?.length < nbResults;

	// Fetch downloads on initial load
	// Use transformDownloads to attach transfer object if active
	useEffect(() => {
		if( downloadsQuery?.isError === true ) {
			ToastService.create('error', 'Failed to load downloads');
		}
		if( downloadsQuery?.data ) {
			let mostRecentLoadAt = (new Date()).getTime();
			let total = getPaginationTotal(downloadsQuery?.data?.headers?.['x-content-range']);
			let updatedDownloads = _.uniqBy(
				downloads.concat(transformDownloads(downloadsQuery?.data?.data || [], transfers)),
				'id'
			);

			const activeDownloads = updatedDownloads.filter((download) => !!download.transfer && download.transfer.status !== 'completed');
			setActive(activeDownloads);

			setDownloads([
				...updatedDownloads
			]);
			setMostRecentLoadAt(mostRecentLoadAt);
			setTotal(total);
		}
	}, [downloadsQuery?.data, downloadsQuery?.isError]);

	return {
		isLoading, isError, refetch, data: downloads, canShowMore, search, currentPage, fetchNextPage, active, setActive
	};
}

/**
 * Transforms downloads and attaches transfer object if active
 * @param {Array} data      data returned by API
 * @param {Array} transfers Aspera transfers
 */
function transformDownloads(data, transfers) {
	let result = [];
	if(!Array.isArray(data)) {
		return result;
	}

	result = data.map((download) => ({
		id: download._id,
		fileName: download.file.name,
		studio: download.asset.studio,
		project: download.project,
		asset: download.asset,
		created: moment.unix(download.created).format('lll'),
		size: 0 // @todo Replace when API supports property
	}));

	transfers.forEach((transfer) => {
		const index = result.findIndex((download) => download.fileName === transfer.fileName);
		if( index !== -1 ) {
			result[index].transfer = transfer;
		}
	});

	return result;
}
